import { create } from "zustand";
import { message } from "antd";
import {
  deleteMasterApi,
  getMasterByIdApi,
  postMasterApi,
  PutMasterApi,
} from "../services/barCodeMaster";
import { useBarcodeStore } from "./useBarcodeMasterStore";

const initialData = {
  divisionName: "All",
  divisionCode: 0,
  departmentCode: 0,
  departmentName: "All",
};

const emptyEditRow = {
  Id: null,
  divisionName: null,
  divisionCode: null,
  departmentCode: null,
  departmentName: null,
  GRN_91_120: "",
  GRN_121_180: "",
  GRN_181_270: "",
  GRN_271_360: "",
  GRN_361_720: "",
  UPDATED_ON: "",
};

export const useBarCodeMasterStore = create((set, get) => ({
  /* ---------- STATE ---------- */
  mode: false,
  formData: initialData,
  errors: {},
  loading: false,
  selectedRow: null,
  editRow: emptyEditRow,

  toggleMode: () => set((state) => ({ mode: !state.mode })),
  setMode: (value) => set({ mode: value }),

  // row select /////
  startEdit: async (row) => {
    set({ loading: true, mode: true });

    try {
      // GET BY ID API CALL
      const response = await getMasterByIdApi(row.Id);
      const data = response?.Data[0];

      set({
        selectedRow: row,
        editRow: {
          Id: data.Id,
          divisionCode: data.DivisionCode,
          divisionName: data.DivisionName,
          departmentCode: data.DepartmentCode,
          departmentName: data.DepartmentName,
          GRN_91_120: data.GRN_91_120,
          GRN_121_180: data.GRN_121_180,
          GRN_181_270: data.GRN_181_270,
          GRN_271_360: data.GRN_271_360,
          GRN_361_720: data.GRN_361_720,
          UPDATED_ON: data.UPDATED_ON,
        },
        errors: {},
      });
    } catch (err) {
      message.error("Failed to load record");
    } finally {
      set({ loading: false });
    }
  },
  updateEditRow: (field, value) =>
    set((state) => ({
      editRow: {
        ...state.editRow,
        [field]: value,
      },
      errors: {
        ...state.errors,
        [field]: "", // clear error on change
      },
    })),

  /* ---------- ROW RESET ---------- */

  resetEditCompletely: () =>
  set({
    selectedRow: null,
    editRow: emptyEditRow,
    errors: {},
    mode: false,
  }),

  resetEdit: () => {
  const { editRow } = get();

  // EDIT MODE: preserve Id & selectedRow
  if (editRow?.Id) {
    set({
      editRow: {
        ...editRow,
        GRN_91_120: "",
        GRN_121_180: "",
        GRN_181_270: "",
        GRN_271_360: "",
        GRN_361_720: "",
      },
      errors: {},
    });
    return;
  }

  //  ADD MODE: full reset
  set({
    selectedRow: null,
    editRow: emptyEditRow,
    errors: {},
  });
},


  deleteEditRow: async () => {
    const { editRow } = get();

    if (!editRow?.Id) return;

    try {
      set({ loading: true });

      await deleteMasterApi(editRow.Id);

      message.success("Record deleted successfully");

      set({
        editRow: emptyEditRow,
        selectedRow: null,
        mode: false,
        errors: {},
      });
    } catch (err) {
      message.error("Delete failed");
    } finally {
      set({ loading: false });
    }
  },

  /* ---------- ROW VALIDATION ---------- */
  validateEditRow: () => {
    const { editRow } = get();
    const errors = {};

    if (!editRow.divisionCode) errors.divisionCode = "Division is required";

    if (!editRow.departmentCode)
      errors.departmentCode = "Department is required";

    const numericFields = [
      "GRN_91_120",
      "GRN_121_180",
      "GRN_181_270",
      "GRN_271_360",
      "GRN_361_720",
    ];

    numericFields.forEach((field) => {
      if (editRow[field] === "" || editRow[field] === null) {
        errors[field] = "Required";
      } else if (isNaN(editRow[field])) {
        errors[field] = "Must be a number";
      }
    });

    set({ errors });
    return Object.keys(errors).length === 0;
  },

  /* ---------- ROW SUBMIT ---------- */
  submitEditRow: async () => {
    const { editRow } = get();

    if (!get().validateEditRow()) {
      message.error("Please fix validation errors");
      return;
    }

    const payload = {
      divisionCode: editRow.divisionCode,
      departmentCode: editRow.departmentCode,
      GRN_91_120: Number(editRow.GRN_91_120),
      GRN_121_180: Number(editRow.GRN_121_180),
      GRN_181_270: Number(editRow.GRN_181_270),
      GRN_271_360: Number(editRow.GRN_271_360),
      GRN_361_720: Number(editRow.GRN_361_720),
      isActive: true,
    };

    try {
      set({ loading: true });

      if (editRow.Id) {
        const response = await PutMasterApi(editRow.Id, payload);
        if (response?.IsSuccess === true) {
          set({
            editRow: emptyEditRow,
            selectedRow: null,
            mode: false,
            errors: {},
          });
        }
      } else {
        const response = await postMasterApi(payload);
        if (response?.IsSuccess === true) {
          set({
            editRow: emptyEditRow,
            selectedRow: null,
            mode: false,
            errors: {},
          });
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      set({ loading: false });
    }
  },

  /* ---------- ACTIONS ---------- */

  handleInputChange: (field, value) =>
    set((state) => ({
      formData: {
        ...state.formData,
        [field]: value,
      },
      errors: {
        ...state.errors,
        [field]: "",
      },
    })),

  resetForm: () =>
    set({
      formData: initialData,
      errors: {},
    }),

  validateForm: () => {
    const { formData } = get();
    const errors = {};

    if (formData.divisionCode === null || formData.divisionCode === undefined)
      errors.divisionCode = "Division is required";

    set({ errors });
    return Object.keys(errors).length === 0;
  },

  fetchDepartment: async (divisionCode, getDepartment) => {
    if (!divisionCode) return;

    try {
      set({ loading: true });
      await getDepartment(divisionCode);
    } catch (err) {
      console.error(err);
      message.error("Failed to load departments!");
    } finally {
      set({ loading: false });
    }
  },

  submitFilter: async () => {
    const { formData, validateForm } = get();
    const { getBarcodeMaster } = useBarcodeStore.getState();

    if (!validateForm()) {
      message.error("Please fix validation errors");
      return;
    }

    await getBarcodeMaster(formData.divisionCode, formData.departmentCode);
  },
  fetchListOnLoad: async () => {
    const { formData } = get();

    const { getBarcodeMaster } = useBarcodeStore.getState();

    await getBarcodeMaster(
      formData.divisionCode, // 0 = All
      formData.departmentCode, // 0 = All
    );
  },
}));
